/**
 * AIDrumGeneratorWrapper Component
 * Wrapper for AI Drum Pattern Generator with track integration logic
 */

import React from 'react';
import { AIDrumPatternGenerator } from '../ai';
import { toast } from 'sonner';

const DRUM_MIDI_MAP = {
  kick: 36,
  snare: 38,
  hihat: 42,
  hihat_open: 46,
  clap: 39,
  tom_high: 50,
  tom_mid: 47,
  tom_low: 45,
  crash: 49,
  ride: 51,
};

export const AIDrumGeneratorWrapper = ({
  isOpen,
  onClose,
  audioContext,
  bpm,
  tracks,
  updateTrack,
  setTracks,
  TRACK_COLORS,
}) => {
  const handleInsertPattern = (patternData, metadata) => {
    const stepsPerBar = 16;
    const totalSteps = metadata.bars * stepsPerBar;
    const stepDuration = (60 / metadata.tempo) / 4; // 16th note in seconds
    
    const midiNotes = [];
    
    // Convert pattern to MIDI notes
    Object.entries(patternData.pattern || {}).forEach(([drumType, steps]) => {
      const midiNote = DRUM_MIDI_MAP[drumType];
      if (!midiNote) return;
      
      steps.forEach((hit, stepIndex) => {
        if (hit === 1) {
          const velocity = patternData.velocities?.[drumType]?.[stepIndex] || 100;
          midiNotes.push({
            note: midiNote,
            velocity,
            startTime: stepIndex * stepDuration,
            duration: stepDuration * 0.5, // Short duration for drums
          });
        }
      });
    });
    
    // Find or create a drum track
    const drumTrack = tracks.find(t => t.type === 'drums' || t.name?.toLowerCase().includes('drum'));
    
    if (drumTrack) {
      // Add to existing drum track
      updateTrack(drumTrack.id, {
        midiEvents: [...(drumTrack.midiEvents || []), ...midiNotes],
      });
      toast.success(`Added ${metadata.genre} beat to ${drumTrack.name}`);
    } else {
      // Create new drum track
      const newTrack = {
        id: `track-${Date.now()}`,
        name: `Drums (${metadata.genre}${metadata.subgenre ? ' - ' + metadata.subgenre : ''})`,
        type: 'drums',
        volume: 0.8,
        pan: 0,
        mute: false,
        solo: false,
        armed: false,
        color: TRACK_COLORS[tracks.length % TRACK_COLORS.length],
        midiEvents: midiNotes,
        midiClips: [{
          id: `clip-${Date.now()}`,
          name: `${metadata.genre} Beat`,
          startTime: 0,
          duration: totalSteps * stepDuration,
          notes: midiNotes,
        }],
      };
      
      setTracks(prev => [...prev, newTrack]);
      toast.success(`Created new drum track with ${metadata.genre} pattern`);
    }
  };

  return (
    <AIDrumPatternGenerator
      isOpen={isOpen}
      onClose={onClose}
      audioContext={audioContext}
      bpm={bpm}
      onInsertPattern={handleInsertPattern}
    />
  );
};

export default AIDrumGeneratorWrapper;
